// BatteryStationLayer — Wave 14cm
// ================================
//
// Rendert Akku-Wechsel-Stationen für VL-Kunden auf der Karte. Jede Station
// wird als DOM-Marker dargestellt (kein Symbol-Layer, weil wir pro Slot
// eine farbige Pille zeigen wollen — das in PNG-Atlas zu backen wäre für
// die ~20 Stationen Overkill).
//
// Pille-Geometrie:
//   - lang (≈40px breit)  → 1060 Wh Akku
//   - kurz (≈26px breit)  → 800 Wh Akku
//   - Farbe nach SoC (siehe socColor)
//   - durchgestrichen wenn reserviert (Wave 14cn-aktiv)
//
// Tap auf eine Station → ruft props.onSelect, der Caller (MapPage) zeigt
// das Detail-Bottom-Sheet.

import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import {
  isSlotReserved,
  socColor,
  type BatteryStation,
  type BatterySlot,
} from '../lib/batteryStations';

interface Props {
  map: maplibregl.Map | null;
  stations: BatteryStation[];
  onSelect: (station: BatteryStation) => void;
  /** Wave 14cw: IDs der Stationen die einen "Du hast mich getapt"-Rahmen
   *  kriegen sollen. CreateCustomPage setzt das wenn die Station als Pin
   *  gesnappt wurde. */
  highlightedIds?: Set<string>;
}

export function BatteryStationLayer({
  map,
  stations,
  onSelect,
  highlightedIds,
}: Props) {
  const markersRef = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    if (!map) return;
    // Cleanup vorheriger Marker bevor wir neue zeichnen — bei Daten-Update.
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];

    for (const st of stations) {
      const isHighlighted = highlightedIds?.has(st.id) ?? false;
      const el = buildStationElement(st, isHighlighted);
      // Click stoppt propagation, sonst würde die Map den Click als
      // "deselect trail" interpretieren.
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        onSelect(st);
      });
      const marker = new maplibregl.Marker({
        element: el,
        // Wave 14dc: zurück auf anchor='bottom' damit der Chevron-Tail
        // unter dem Label EXAKT auf den GPS-Punkt zeigt — User-Wunsch:
        // "kannst du bitte unter dem namen so einen pin machen der GENAU
        // auf die location zeigt". Vorher (14cq) anchor='center' zentrierte
        // die ganze Box auf dem Punkt, man sah nicht wo die Station genau
        // ist + die großen Markers verdeckten Ortsnamen darunter.
        // Hit-Box-Bug von 14cn (negative margin) ist hier nicht relevant
        // weil wir das Padding regulär halten (kein negativer Trick).
        anchor: 'bottom',
        // opacityWhenCovered: 1 — Stationen sollen auch hinter 3D-Terrain
        // sichtbar bleiben, sie sind ein wichtiges Pflicht-Feature für VL.
        opacityWhenCovered: '1',
      })
        .setLngLat([st.lon, st.lat])
        .addTo(map);
      markersRef.current.push(marker);
    }

    return () => {
      for (const m of markersRef.current) m.remove();
      markersRef.current = [];
    };
  }, [map, stations, onSelect, highlightedIds]);

  return null;
}

/**
 * Baut das DOM-Element für eine Station-Pille-Reihe. Sieht aus wie:
 *
 *   ┌──────────────┐
 *   │ ▮▮ ▯ ▮▮ ▯  │   ← farbige Pillen je nach SoC, lang/kurz je nach Wh
 *   │  Bar Centrale│   ← name darunter (klein)
 *   └──────────────┘
 */
function buildStationElement(
  st: BatteryStation,
  isHighlighted: boolean,
): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'adt-battery-station-marker';
  wrapper.style.cursor = 'pointer';
  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.alignItems = 'center';
  wrapper.style.gap = '3px';
  wrapper.style.pointerEvents = 'auto';
  // Wave 14cq: Größerer Marker + großzügigerer Tap-Bereich.
  // User-Bug: "klicke auf eine Wechselstation und es wird immer daneben
  // geklickt :( das Feld welches mein Klicken akzeptiert soll so groß
  // sein wie das Logo in der aktuellen Ansicht."
  //
  // Vorher (14cn): visuelles Logo ca. 70px × 30px, Tap-Bereich via
  // padding 14px + negative margin -14px → maplibre's anchor-Math hat
  // sich mit dem negative margin nicht sauber verstanden, effektive
  // Hit-Box war kleiner als die Pille selbst.
  //
  // Wave 14dc: Padding asymmetrisch — oben/seitlich großzügig (Hit-Area),
  // unten 0 damit der Chevron-Tail ganz unten anliegt und mit anchor='bottom'
  // exakt auf den GPS-Punkt zeigt. Click-Area ist immer noch deutlich größer
  // als der Sichtkern.
  wrapper.style.padding = '12px 12px 0 12px';
  wrapper.title = `${st.name} — tap for details`;
  // Wave 14cw: Glow-Rahmen wenn die Station gerade als Pin gesnappt ist
  // (CreateCustom-Flow). Sky-Farbe matched die Pin-Marker-Farbe sonst auf
  // der Karte. transition für sauberen Fade beim Setzen/Entfernen.
  wrapper.style.transition = 'box-shadow 180ms ease, transform 180ms ease';
  if (isHighlighted) {
    wrapper.style.boxShadow =
      '0 0 0 3px rgba(56, 189, 248, 0.95), 0 0 14px 4px rgba(56, 189, 248, 0.55)';
    wrapper.style.borderRadius = '12px';
    wrapper.style.transform = 'scale(1.05)';
  }

  // Pillen-Reihe
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.gap = '3px';
  row.style.padding = '5px 6px';
  row.style.background = 'rgba(20, 20, 20, 0.88)';
  row.style.border = '1px solid rgba(255,255,255,0.25)';
  row.style.borderRadius = '8px';
  row.style.backdropFilter = 'blur(2px)';

  if (st.slots.length === 0) {
    // Leerer-Slot-Indikator — Station angelegt, aber noch keine Akkus.
    const empty = document.createElement('span');
    empty.style.fontSize = '11px';
    empty.style.color = 'rgba(255,255,255,0.55)';
    empty.style.padding = '2px 4px';
    empty.textContent = 'no slots';
    row.appendChild(empty);
  } else {
    // Wave 14cs: gruppiert anzeigen — erst alle grossen Akkus (1060 Wh)
    // links, dann alle kleinen (800 Wh) rechts. Niemals abwechselnd.
    // User-Wunsch: "ist für das gehirn einfacher abzuschätzen wie viele
    // es sind, wenn ich zB irgendwann 5 grosse und 3 kleine habe".
    // Innerhalb derselben Größe nach slotNumber sortiert (stabil + deterministisch).
    const sorted = st.slots.slice().sort((a, b) => {
      if (a.batterySizeWh !== b.batterySizeWh) {
        // 1060 vor 800 → descending
        return b.batterySizeWh - a.batterySizeWh;
      }
      return a.slotNumber - b.slotNumber;
    });
    for (const slot of sorted) {
      row.appendChild(buildSlotPill(slot));
    }
  }

  // Name darunter — Label.
  const label = document.createElement('div');
  label.textContent = st.name;
  label.style.fontSize = '12px';
  label.style.fontWeight = '600';
  label.style.color = '#fff';
  label.style.background = 'rgba(20, 20, 20, 0.88)';
  label.style.padding = '2px 6px';
  label.style.borderRadius = '4px';
  label.style.whiteSpace = 'nowrap';
  label.style.maxWidth = '160px';
  label.style.overflow = 'hidden';
  label.style.textOverflow = 'ellipsis';
  label.style.border = '1px solid rgba(255,255,255,0.18)';

  // Wave 14dc: Chevron-Tail unter dem Label. CSS-Triangle (border-trick)
  // mit der gleichen schwarzen Farbe + weißem Border-Akzent wie das Label.
  // Mit anchor='bottom' auf dem Marker landet der Triangle-Tip genau auf
  // dem GPS-Koord — Standard-Map-Pin-Verhalten.
  const tail = document.createElement('div');
  tail.style.width = '0';
  tail.style.height = '0';
  // Triangle nach unten zeigend: linke + rechte transparente Border,
  // obere Border in der Hintergrundfarbe.
  tail.style.borderLeft = '7px solid transparent';
  tail.style.borderRight = '7px solid transparent';
  tail.style.borderTop = '9px solid rgba(20, 20, 20, 0.88)';
  // Leichtes Overlap mit dem Label damit der Übergang nahtlos aussieht.
  tail.style.marginTop = '-1px';
  // Filter-drop-shadow gibt dem Tail den dünnen weißen Rand wie das Label.
  tail.style.filter = 'drop-shadow(0 1px 0 rgba(255,255,255,0.18))';

  wrapper.appendChild(row);
  wrapper.appendChild(label);
  wrapper.appendChild(tail);
  return wrapper;
}

/** Eine einzelne Pille (1 Slot). 1060 Wh = lang, 800 Wh = kurz.
 *  Wave 14cq: vergrößert von 14×7 / 10×7 auf 24×11 / 16×11 für bessere
 *  Sichtbarkeit aus dem Sattel (und damit der Tap-Bereich des Markers
 *  insgesamt deutlich wächst — User-Wunsch). */
function buildSlotPill(slot: BatterySlot): HTMLSpanElement {
  const isLarge = slot.batterySizeWh === 1060;
  const reserved = isSlotReserved(slot);

  const pill = document.createElement('span');
  pill.style.display = 'inline-block';
  pill.style.width = isLarge ? '24px' : '16px';
  pill.style.height = '11px';
  pill.style.background = socColor(slot.currentSoc);
  pill.style.borderRadius = '4px';
  pill.style.position = 'relative';
  pill.title = `Slot ${slot.slotNumber}: ${slot.batterySizeWh} Wh · ${slot.currentSoc}%${
    reserved ? ' · RESERVED' : ''
  }`;

  if (reserved) {
    // Durchgestrichen: roter diagonaler Strich über die Pille.
    const strike = document.createElement('span');
    strike.style.position = 'absolute';
    strike.style.inset = '0';
    strike.style.background =
      'linear-gradient(to top right, transparent 45%, rgba(220, 38, 38, 0.95) 47%, rgba(220, 38, 38, 0.95) 53%, transparent 55%)';
    strike.style.borderRadius = '4px';
    pill.appendChild(strike);
  }

  return pill;
}
